import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Funnel, CalendarBlank } from '@phosphor-icons/react';
import { Calendar } from '@/components/ui/calendar';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

// ── Filter shape ─────────────────────────────────────────────────────────────

export interface EmailLogFilters {
  addressQ: string;      // ILIKE address search
  kind: string;          // exact kind match; empty = all
  createdAfter: string;  // YYYY-MM-DDTHH:mm
  createdBefore: string; // YYYY-MM-DDTHH:mm
}

export const EMPTY_EMAIL_LOG_FILTERS: EmailLogFilters = {
  addressQ: '',
  kind: '',
  createdAfter: '',
  createdBefore: '',
};

export function countActiveEmailLogFilters(f: EmailLogFilters): number {
  let n = 0;
  if (f.addressQ) n++;
  if (f.kind) n++;
  if (f.createdAfter) n++;
  if (f.createdBefore) n++;
  return n;
}

// ── Panel ─────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: EmailLogFilters;
  onChange: (f: EmailLogFilters) => void;
}

export function EmailLogFilterPanel({ open, onOpenChange, filters, onChange }: Props) {
  const { t } = useTranslation('common');
  const activeCount = countActiveEmailLogFilters(filters);

  const KIND_OPTIONS = [
    { value: '', label: t('settings.log.email.filter.kindAll') },
    { value: 'newsletter', label: t('settings.log.email.kind.newsletter') },
    { value: 'invitation', label: t('settings.log.email.kind.invitation') },
    { value: 'other', label: t('settings.log.email.kind.other') },
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[22rem]"
      >
        {/* ── Header ── */}
        <div className="flex shrink-0 items-center gap-2 border-b border-border px-5 py-4">
          <Funnel className="h-4 w-4 text-muted-foreground" />
          <SheetTitle className="font-heading text-base font-bold">
            {t('settings.log.email.filter.title')}
          </SheetTitle>
          {activeCount > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-pill bg-primary px-1.5 font-heading text-[0.65rem] font-bold text-primary-foreground">
              {activeCount}
            </span>
          )}
          <SheetDescription className="sr-only">
            {t('settings.log.email.filter.description')}
          </SheetDescription>
        </div>

        {/* ── Scrollable sections ── */}
        <div className="flex-1 divide-y divide-border overflow-y-auto">

          {/* Date range */}
          <FilterSection title={t('settings.log.email.filter.dateRange.title')}>
            <div className="flex flex-col gap-3">
              <DateTimeField
                label={t('settings.log.email.filter.dateRange.from')}
                value={filters.createdAfter}
                {...(filters.createdBefore ? { max: filters.createdBefore } : {})}
                onChange={(v) => onChange({ ...filters, createdAfter: v })}
              />
              <DateTimeField
                label={t('settings.log.email.filter.dateRange.to')}
                value={filters.createdBefore}
                {...(filters.createdAfter ? { min: filters.createdAfter } : {})}
                onChange={(v) => onChange({ ...filters, createdBefore: v })}
              />
            </div>
          </FilterSection>

          {/* Email address */}
          <FilterSection title={t('settings.log.email.filter.address.title')}>
            <input
              type="search"
              value={filters.addressQ}
              onChange={(e) => onChange({ ...filters, addressQ: e.target.value })}
              placeholder={t('settings.log.email.filter.search.placeholder')}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            {filters.addressQ && (
              <button
                type="button"
                onClick={() => onChange({ ...filters, addressQ: '' })}
                className="self-start font-heading text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none"
              >
                <X className="mr-0.5 inline h-3 w-3" />
                {t('settings.log.email.filter.clearSection')}
              </button>
            )}
          </FilterSection>

          {/* Kind */}
          <FilterSection title={t('settings.log.email.filter.kind')}>
            <div className="flex flex-col gap-1.5">
              {KIND_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onChange({ ...filters, kind: opt.value })}
                  className={cn(
                    'flex items-center rounded-lg border px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    filters.kind === opt.value
                      ? 'border-primary bg-primary/10 font-heading font-semibold text-foreground'
                      : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground',
                  )}
                >
                  <span
                    className={cn(
                      'mr-2.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border',
                      filters.kind === opt.value
                        ? 'border-primary bg-primary'
                        : 'border-border',
                    )}
                  >
                    {filters.kind === opt.value && (
                      <span className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
                    )}
                  </span>
                  {opt.label}
                </button>
              ))}
            </div>
          </FilterSection>
        </div>

        {/* ── Footer ── */}
        {activeCount > 0 && (
          <div className="shrink-0 border-t border-border px-5 py-4">
            <button
              type="button"
              onClick={() => onChange(EMPTY_EMAIL_LOG_FILTERS)}
              className="w-full rounded-pill border border-border py-2 font-heading text-sm font-semibold text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('settings.log.email.filter.clearAll')}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function FilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3 px-5 py-4">
      <h3 className="font-heading text-[0.7rem] font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {children}
    </div>
  );
}

function DateTimeField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: string;
  min?: string;
  max?: string;
  onChange: (v: string) => void;
}) {
  const { t, i18n } = useTranslation('common');
  const [open, setOpen] = useState(false);

  const datePart = value ? value.split('T')[0] : '';
  const timePart = value ? (value.split('T')[1] || '00:00') : '00:00';

  const displayValue = datePart
    ? new Intl.DateTimeFormat(i18n.language, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(new Date(`${datePart}T${timePart}`))
    : t('settings.log.email.filter.dateRange.pick');

  function handleDateSelect(d: string) {
    onChange(`${d}T${timePart}`);
  }

  const [hourStr = '00', minStr = '00'] = timePart.split(':');
  const hourNum = Math.max(0, Math.min(23, parseInt(hourStr, 10) || 0));
  const minNum = Math.max(0, Math.min(59, parseInt(minStr, 10) || 0));

  function handleHourChange(e: React.ChangeEvent<HTMLInputElement>) {
    const h = Math.max(0, Math.min(23, parseInt(e.target.value || '0', 10)));
    const newTime = `${String(h).padStart(2, '0')}:${String(minNum).padStart(2, '0')}`;
    if (datePart) onChange(`${datePart}T${newTime}`);
  }

  function handleMinChange(e: React.ChangeEvent<HTMLInputElement>) {
    const m = Math.max(0, Math.min(59, parseInt(e.target.value || '0', 10)));
    const newTime = `${String(hourNum).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    if (datePart) onChange(`${datePart}T${newTime}`);
  }

  return (
    <div className="flex flex-col gap-1.5">
      <span className="font-heading text-xs font-semibold text-muted-foreground">{label}</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              datePart
                ? 'border-primary font-heading font-semibold text-foreground'
                : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground',
            )}
          >
            <CalendarBlank className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1 text-left">{displayValue}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-2" side="bottom" sideOffset={4}>
          <Calendar
            value={datePart || undefined}
            onSelect={handleDateSelect}
            min={min?.split('T')[0]}
            max={max?.split('T')[0]}
            locale={i18n.language}
          />
          <div className="border-t border-border px-1 pt-2">
            <div
              className={cn(
                'flex items-center justify-center gap-1 rounded-lg border border-border bg-background px-3 py-1.5 text-sm tabular-nums',
                !datePart && 'pointer-events-none opacity-40',
              )}
            >
              <input
                type="number"
                min={0}
                max={23}
                value={hourNum}
                disabled={!datePart}
                onChange={handleHourChange}
                className="w-8 bg-transparent text-center focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                aria-label="hours"
              />
              <span className="select-none text-muted-foreground">:</span>
              <input
                type="number"
                min={0}
                max={59}
                value={minNum}
                disabled={!datePart}
                onChange={handleMinChange}
                className="w-8 bg-transparent text-center focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                aria-label="minutes"
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="self-start font-heading text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none"
        >
          <X className="mr-0.5 inline h-3 w-3" />
          {t('settings.log.email.filter.clearSection')}
        </button>
      )}
    </div>
  );
}
